import {getLogger} from '@/shared/utils'
import {getFileMetadata} from '../../services/fileMetadata'
import {type ExternalViewerConfig, ViewerDataSource} from './handler-structures'

/**
 * Version of the `/metadata` contract this build reads. Stamped onto every config the open flow
 * produces, and compared against on restore.
 *
 * A restored tab is the only way a config outlives the request that produced it: `getFileMetadata`
 * is called from the open flow and nowhere else, and the whole config — `controllerInfo` included —
 * is persisted to localStorage verbatim. So a tab opened before the OBserver started sending a
 * field keeps the older answer for as long as it stays open, across any number of reloads.
 *
 * Bump this whenever the frontend starts depending on something a config persisted by an older
 * build would not carry.
 *
 * 1 — `controllerInfo.workbenchProperties` advertises the export formats the controller allows
 *     (`SAVE_AS_*_ALLOWED`), which the export strip intersects with what the iframe reports. Tabs
 *     persisted before that showed no export icons at all, permanently: the intersection was taken
 *     against an empty set and nothing ever re-asked the server.
 */
export const VIEWER_METADATA_SCHEMA = 1

/** Whether this config's `controllerInfo` predates the current schema and has to be re-requested. */
export function isMetadataStale(config: ExternalViewerConfig): boolean {
  return (config.metadataSchema ?? 0) < VIEWER_METADATA_SCHEMA
}

/**
 * Whether a refresh is possible and worth making:
 *
 *  - placeholders have no server answer to refresh (and the open flow is already fetching one);
 *  - without a `sourcePath` there is nothing to re-request by — configs persisted before that field
 *    existed are stuck with what they have. They are left alone rather than dropped: the viewer
 *    still works off its persisted endpoint, and closing a tab the user had open is a worse outcome
 *    than a missing export icon;
 *  - STORAGE viewers read the bytes from storage themselves. Their `controllerInfo` carries no
 *    controller endpoint and no export properties (and the managers never probe them), so there is
 *    nothing a refresh could change — and plain documents are resolved entirely in the browser, so
 *    `/metadata` has never seen them and would 404.
 */
export function canRefreshMetadata(config: ExternalViewerConfig): boolean {
  if (config.loading || config.error || !config.sourcePath) {
    return false
  }
  const dataSource = config.controllerInfo?.dataSource ?? ViewerDataSource.CONTROLLER
  return dataSource === ViewerDataSource.CONTROLLER
}

export function needsMetadataRefresh(config: ExternalViewerConfig): boolean {
  return isMetadataStale(config) && canRefreshMetadata(config)
}

/**
 * Re-request `/metadata` for a restored config and return a copy carrying the server's current
 * answer, or null when the refresh was impossible, failed, or resolved no viewer.
 *
 * `controllerInfo` is replaced wholesale rather than merged field by field: it is entirely
 * server-derived, so a partial update would leave the config in a state the OBserver never
 * produced. `objectInfo` is kept exactly as it was — it is the tab's identity (the managers dedupe
 * tabs and key iframes by `objectInfo.id`), and the two `/metadata` paths do not agree on it.
 *
 * Swapping in a fresh `controllerInfo` does not reload the iframe: its src is built from the viewer
 * app name and version only (see ExternalViewer.render), both unchanged in the ordinary case.
 */
export async function refreshViewerMetadata(
  config: ExternalViewerConfig
): Promise<ExternalViewerConfig | null> {
  if (!canRefreshMetadata(config) || !config.sourcePath) {
    return null
  }
  try {
    // A tab opened from a folder someone else shares asks through that share; any other asks as before.
    const fresh = await (config.share
      ? getFileMetadata(config.sourcePath, config.formatHint, config.share)
      : getFileMetadata(config.sourcePath, config.formatHint))
    // Same guard the open flow applies: some backends answer 200 with an empty controllerInfo, and
    // adopting that would break a tab that is working off its persisted one.
    if (!fresh?.controllerInfo?.viewerAppName) {
      getLogger().warn(
        `Metadata refresh for ${config.sourcePath} resolved no viewer; keeping the persisted controller info.`
      )
      return null
    }
    return {
      ...config,
      controllerInfo: fresh.controllerInfo as unknown as ExternalViewerConfig['controllerInfo'],
      metadataSchema: VIEWER_METADATA_SCHEMA,
    }
  } catch (error) {
    // Non-fatal by design: the tab keeps working off its persisted controllerInfo, and the refresh
    // is retried the next time the page is loaded.
    getLogger().error(`Failed to refresh viewer metadata for ${config.sourcePath}:`, error)
    return null
  }
}

/**
 * Refresh every config in `configs` whose persisted metadata is stale, reporting each success to
 * `onRefreshed`.
 *
 * `attempted` is the caller's own set of `objectInfo.id`s, so a config is tried at most once per
 * session: a failed refresh is not retried on every re-render, and a successful one is no longer
 * stale. Callers pass only the viewers that have actually started, which bounds a restored session
 * to one request per tab the user really looks at.
 */
export function refreshStaleMetadata(params: {
  configs: readonly ExternalViewerConfig[]
  attempted: Set<string>
  onRefreshed: (refreshed: ExternalViewerConfig) => void
}): void {
  const {configs, attempted, onRefreshed} = params
  for (const config of configs) {
    const id = config.objectInfo?.id != null ? String(config.objectInfo.id) : ''
    if (!id || attempted.has(id) || !needsMetadataRefresh(config)) {
      continue
    }
    attempted.add(id)
    void refreshViewerMetadata(config).then((refreshed) => {
      if (refreshed) {
        onRefreshed(refreshed)
      }
    })
  }
}
